import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import cameraIcon from '../assets/images/camera-icon.png'
import { uuidSmall } from '../helpers'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

const UploadPicture = ({
  title,
  description,
  imageSrc,
  onSubmit,
  ImageCropComponent
}) => {
  const { t } = useTranslation()
  const inputFile = useRef(null)

  const [uuidInput, setUuidInput] = useState(uuidSmall())
  const [displayImageCrop, setDisplayImageCrop] = useState(false)
  const [localImage, setLocalImage] = useState(null)
  const [error, setError] = useState(null)

  const openFileDialog = () => {
    inputFile.current.click()
  }

  const handleSubmit = (event) => {
    setError(null)

    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('upload_picture.error_invalid_type'))
      setUuidInput(uuidSmall())
      return
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(t('upload_picture.error_file_too_large'))
      setUuidInput(uuidSmall())
      return
    }

    // reset file input
    setUuidInput(uuidSmall())

    if (ImageCropComponent) {
      setDisplayImageCrop(true)
      setLocalImage(file)
    } else {
      onSubmit(event)
    }
  }

  return (
    <>
      <div>
        <label className='block text-sm font-medium text-gray-700'>
          {title}
        </label>
        <div className='relative mt-2 flex items-center'>
          {imageSrc ? (
            <img
              className='relative mr-4 h-32 w-32 rounded-full border-2 border-dotted border-gray-300 object-contain'
              src={`${imageSrc}?random_number=${new Date().getTime()}`}
              alt=''
              onError={() => setError(t('upload_picture.error_load_failed'))}
            />
          ) : (
            <div className='relative mr-4 h-32 w-32 rounded-full border-2 border-dotted border-gray-300' />
          )}
          <input
            key={uuidInput}
            type='file'
            accept='image/jpeg,image/jpg,image/png,image/webp'
            id='file'
            ref={inputFile}
            style={{ display: 'none' }}
            onChange={handleSubmit}
          />
          <div
            className='absolute bottom-0 left-24 h-12 w-12'
            onClick={openFileDialog}
          >
            <img src={cameraIcon} className='cursor-pointer' alt='' />
          </div>
        </div>
        {error && (
          <p className='mt-2 text-sm font-medium text-red-600'>{error}</p>
        )}
        {description && (
          <p className='mt-2 text-xs italic text-gray-700'>{description}</p>
        )}
      </div>
      {ImageCropComponent && (
        <ImageCropComponent
          open={displayImageCrop}
          close={() => {
            setLocalImage(null)
            setDisplayImageCrop(false)
            setError(null)
          }}
          localImage={localImage}
          onSubmit={onSubmit}
          onError={(errorMessage) => {
            setError(errorMessage)
            setLocalImage(null)
            setDisplayImageCrop(false)
          }}
        />
      )}
    </>
  )
}

UploadPicture.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  imageSrc: PropTypes.string,
  onSubmit: PropTypes.func,
  ImageCropComponent: PropTypes.any
}

UploadPicture.defaultProps = {
  title: '',
  description: undefined,
  imageSrc: undefined,
  onSubmit: () => {},
  ImageCropComponent: null
}

export default UploadPicture
